/**
 * Platform feature switches from GET /api/features (backend config/featureFlags.ts).
 * Fetched once per page load and shared. Until it loads, and if it fails,
 * everything gated here stays hidden.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';

export interface FeatureFlags {
  /** ACORD forms, certificates of insurance and certificate holders. */
  acord: boolean;
}

const OFF: FeatureFlags = { acord: false };
let cached: FeatureFlags | null = null;
let pending: Promise<FeatureFlags> | null = null;

function load(): Promise<FeatureFlags> {
  if (!pending) {
    pending = apiClient
      .get<FeatureFlags>('/api/features')
      .then((flags) => (cached = { ...OFF, ...flags }))
      .catch(() => {
        pending = null; // retry on the next mount
        return OFF;
      });
  }
  return pending;
}

export function useFeatureFlags(): FeatureFlags {
  const [flags, setFlags] = useState<FeatureFlags>(cached || OFF);
  useEffect(() => {
    if (cached) return;
    let alive = true;
    load().then((f) => alive && setFlags(f));
    return () => {
      alive = false;
    };
  }, []);
  return flags;
}
